import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useIntl } from "react-intl";
import { useOkapiKy, useCallout } from "@folio/stripes/core";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { Accordion, AccordionSet, Modal, ModalFooter, Button, Checkbox, Loading, MessageBanner, RadioButton, RadioButtonGroup, TextField, TextArea, } from "@folio/stripes/components";
import { fetchTitleById } from "../../api/titleService";
import { previewPreLitigation, createPreLitigation, downloadPreLitigationReport, } from "../../api/enforcementService";
import { CHANNEL_LABEL_IDS } from "../../types/enforcement";
import { VOLUME_COLORS } from "../../utils/volumeConstants";
import { formatIsbn } from "../../utils/isbn";
import styles from "./PreLitigationDialog.module.css";
import { invalidateDepositRecordViews } from "../../utils/depositRecordQueries";
var URGED_BADGE_STYLE = {
    background: VOLUME_COLORS.urged.bg,
    color: VOLUME_COLORS.urged.color,
};
var NOT_URGED_BADGE_STYLE = {
    background: VOLUME_COLORS.missing.bg,
    color: VOLUME_COLORS.missing.color,
};
var PRE_LITIGATION_BADGE_STYLE = {
    background: VOLUME_COLORS.preLitigation.bg,
    color: VOLUME_COLORS.preLitigation.color,
};
var titleText = function (title) {
    var parts = [title.titleName];
    if (title.isbn)
        parts.push(formatIsbn(title.isbn));
    if (title.publicationYear)
        parts.push(String(title.publicationYear));
    return parts.join(" · ");
};
export var PreLitigationDialog = function (_a) {
    var _b, _c, _d, _e, _f;
    var open = _a.open, onClose = _a.onClose, titleId = _a.titleId;
    var _g = useIntl(), formatMessage = _g.formatMessage, formatDate = _g.formatDate;
    var kyInstance = useOkapiKy();
    var queryClient = useQueryClient();
    var callout = useCallout();
    var _h = useState("select"), phase = _h[0], setPhase = _h[1];
    var _j = useState(new Set()), selectedTitleIds = _j[0], setSelectedTitleIds = _j[1];
    var _k = useState("POSTAL"), channel = _k[0], setChannel = _k[1];
    var _l = useState(""), recipientAddress = _l[0], setRecipientAddress = _l[1];
    var _m = useState(""), notes = _m[0], setNotes = _m[1];
    var _o = useState(false), initialized = _o[0], setInitialized = _o[1];
    var _p = useState(true), urgedOpen = _p[0], setUrgedOpen = _p[1];
    var _q = useState(true), preLitigationOpen = _q[0], setPreLitigationOpen = _q[1];
    var _r = useState(false), notUrgedOpen = _r[0], setNotUrgedOpen = _r[1];
    var titleQuery = useQuery(["pre-litigation-title", titleId], function () { return fetchTitleById(kyInstance, titleId); }, { enabled: open && !!titleId });
    var publisherId = (_c = (_b = titleQuery.data) === null || _b === void 0 ? void 0 : _b.ownerId) !== null && _c !== void 0 ? _c : null;
    var previewQuery = useQuery(["pre-litigation-preview", publisherId], function () {
        return previewPreLitigation(kyInstance, publisherId);
    }, { enabled: open && !!publisherId });
    var preview = (_d = previewQuery.data) !== null && _d !== void 0 ? _d : null;
    var isLoading = (open && !!titleId && titleQuery.isLoading) ||
        (open && !!publisherId && previewQuery.isLoading);
    var preLitigationTitles = useMemo(function () { var _a; return ((_a = preview === null || preview === void 0 ? void 0 : preview.titles) !== null && _a !== void 0 ? _a : []).filter(function (t) { return t.hasActivePreLitigation; }); }, [preview]);
    var urgedTitles = useMemo(function () {
        var _a;
        return ((_a = preview === null || preview === void 0 ? void 0 : preview.titles) !== null && _a !== void 0 ? _a : []).filter(function (t) { return t.hasActiveUrgence && !t.hasActivePreLitigation; });
    }, [preview]);
    var notUrgedTitles = useMemo(function () {
        var _a;
        return ((_a = preview === null || preview === void 0 ? void 0 : preview.titles) !== null && _a !== void 0 ? _a : []).filter(function (t) { return !t.hasActiveUrgence && !t.hasActivePreLitigation; });
    }, [preview]);
    var dataBoxContact = useMemo(function () { var _a, _b; return (_b = ((_a = preview === null || preview === void 0 ? void 0 : preview.contacts) !== null && _a !== void 0 ? _a : []).find(function (c) { return c.type === "DATA_BOX"; })) !== null && _b !== void 0 ? _b : null; }, [preview]);
    var selectedDepositRecordIds = useMemo(function () {
        var _a;
        return ((_a = preview === null || preview === void 0 ? void 0 : preview.titles) !== null && _a !== void 0 ? _a : [])
            .filter(function (t) { return selectedTitleIds.has(t.titleId); })
            .flatMap(function (t) { var _a; return (_a = t.depositRecordIds) !== null && _a !== void 0 ? _a : []; });
    }, [preview, selectedTitleIds]);
    useEffect(function () {
        var _a;
        if (open &&
            !initialized &&
            previewQuery.isSuccess &&
            !previewQuery.isFetching &&
            preview) {
            setSelectedTitleIds(new Set(urgedTitles.map(function (t) { return t.titleId; })));
            setRecipientAddress((_a = preview.postalAddress) !== null && _a !== void 0 ? _a : "");
            setInitialized(true);
        }
    }, [
        open,
        initialized,
        previewQuery.isSuccess,
        previewQuery.isFetching,
        preview,
        urgedTitles,
    ]);
    useEffect(function () {
        if (!open) {
            setPhase("select");
            setSelectedTitleIds(new Set());
            setChannel("POSTAL");
            setRecipientAddress("");
            setNotes("");
            setInitialized(false);
            setUrgedOpen(true);
            setPreLitigationOpen(true);
            setNotUrgedOpen(false);
        }
    }, [open]);
    var toggleTitle = useCallback(function (id) {
        setSelectedTitleIds(function (prev) {
            var next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            }
            else {
                next.add(id);
            }
            return next;
        });
    }, []);
    var createMutation = useMutation(function (payload) { return createPreLitigation(kyInstance, payload); }, {
        onSuccess: function (result) {
            queryClient.invalidateQueries(["titlesWithIsbn"]);
            queryClient.invalidateQueries(["titlesWithoutIsbn"]);
            invalidateDepositRecordViews(queryClient);
            callout === null || callout === void 0 ? void 0 : callout.sendCallout({
                type: "success",
                message: formatMessage({ id: "ui-br-legaldeposits.preLitigation.callout.success" }, { count: result.createdCount }),
            });
            onClose();
        },
    });
    var downloadMutation = useMutation(function (ids) {
        return downloadPreLitigationReport(kyInstance, ids);
    });
    var handleDownload = useCallback(function () { return downloadMutation.mutate(selectedDepositRecordIds); }, [downloadMutation, selectedDepositRecordIds]);
    var handleSubmit = useCallback(function () {
        var _a;
        if (!publisherId)
            return;
        var recipient = channel === "POSTAL"
            ? recipientAddress.trim()
            : ((_a = dataBoxContact === null || dataBoxContact === void 0 ? void 0 : dataBoxContact.value) !== null && _a !== void 0 ? _a : "");
        createMutation.mutate({
            publisherId: publisherId,
            depositRecordIds: selectedDepositRecordIds,
            communicationType: channel,
            recipientAddress: recipient,
            notes: notes.trim() || undefined,
        });
    }, [
        publisherId,
        channel,
        recipientAddress,
        dataBoxContact,
        selectedDepositRecordIds,
        notes,
        createMutation,
    ]);
    var hasSelection = selectedDepositRecordIds.length > 0;
    var canSubmit = hasSelection &&
        !createMutation.isLoading &&
        (channel === "POSTAL"
            ? recipientAddress.trim().length > 0
            : !!dataBoxContact);
    var renderTitleRow = function (title, badgeStyle, badgeText) {
        var _a, _b, _c;
        return (React.createElement("div", { key: title.titleId },
            React.createElement(Checkbox, { checked: selectedTitleIds.has(title.titleId), onChange: function () { return toggleTitle(title.titleId); }, label: React.createElement("span", { className: styles.titleRow },
                    React.createElement("span", null, titleText(title)),
                    React.createElement("span", { className: styles.badge, style: badgeStyle }, badgeText)) }),
            ((_b = (_a = title.issues) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0) > 0 && (React.createElement("ul", { className: styles.issueList, "data-test-id": "pre-litigation-issues" }, ((_c = title.issues) !== null && _c !== void 0 ? _c : []).map(function (issue) {
                var _a, _b;
                return (React.createElement("li", { key: issue.issueId }, formatMessage({ id: "ui-br-legaldeposits.preLitigation.issueCopies" }, {
                    issue: issue.label,
                    count: (_b = (_a = issue.depositRecordIds) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0,
                })));
            })))));
    };
    var preLitigationBadgeText = function (title) {
        return title.lastPreLitigationSentDate
            ? formatMessage({ id: "ui-br-legaldeposits.preLitigation.badge.sentOn" }, { date: formatDate(title.lastPreLitigationSentDate) })
            : formatMessage({ id: "ui-br-legaldeposits.preLitigation.badge.sent" });
    };
    var footer = (React.createElement(ModalFooter, null, phase === "select" ? (React.createElement(React.Fragment, null,
        React.createElement(Button, { buttonStyle: "primary", onClick: function () { return setPhase("record"); }, disabled: !hasSelection }, formatMessage({ id: "ui-br-legaldeposits.button.continue" })),
        React.createElement(Button, { onClick: onClose }, formatMessage({ id: "ui-br-legaldeposits.button.cancel" })))) : (React.createElement(React.Fragment, null,
        React.createElement(Button, { buttonStyle: "primary", onClick: handleSubmit, disabled: !canSubmit }, formatMessage({
            id: "ui-br-legaldeposits.preLitigation.button.record",
        })),
        React.createElement(Button, { onClick: function () { return setPhase("select"); } }, formatMessage({ id: "ui-br-legaldeposits.button.back" })),
        React.createElement(Button, { onClick: onClose }, formatMessage({ id: "ui-br-legaldeposits.button.cancel" }))))));
    return (React.createElement(Modal, { open: open, label: formatMessage({
            id: "ui-br-legaldeposits.preLitigation.dialog.title",
        }), onClose: onClose, size: "medium", footer: footer }, isLoading ? (React.createElement(Loading, null)) : phase === "select" ? (((_f = (_e = preview === null || preview === void 0 ? void 0 : preview.titles) === null || _e === void 0 ? void 0 : _e.length) !== null && _f !== void 0 ? _f : 0) > 0 ? (React.createElement(React.Fragment, null,
        React.createElement(AccordionSet, null,
            urgedTitles.length > 0 && (React.createElement(Accordion, { id: "pre-litigation-urged", label: formatMessage({ id: "ui-br-legaldeposits.preLitigation.group.urged" }, { count: urgedTitles.length }), open: urgedOpen, onToggle: function () { return setUrgedOpen(function (prev) { return !prev; }); } }, urgedTitles.map(function (t) {
                return renderTitleRow(t, URGED_BADGE_STYLE, formatMessage({
                    id: "ui-br-legaldeposits.detail.volume.urged",
                }));
            }))),
            preLitigationTitles.length > 0 && (React.createElement(Accordion, { id: "pre-litigation-sent", label: formatMessage({ id: "ui-br-legaldeposits.preLitigation.group.sent" }, { count: preLitigationTitles.length }), open: preLitigationOpen, onToggle: function () { return setPreLitigationOpen(function (prev) { return !prev; }); } }, preLitigationTitles.map(function (t) {
                return renderTitleRow(t, PRE_LITIGATION_BADGE_STYLE, preLitigationBadgeText(t));
            }))),
            notUrgedTitles.length > 0 && (React.createElement(Accordion, { id: "pre-litigation-not-urged", label: formatMessage({ id: "ui-br-legaldeposits.preLitigation.group.notUrged" }, { count: notUrgedTitles.length }), open: notUrgedOpen, onToggle: function () { return setNotUrgedOpen(function (prev) { return !prev; }); } }, notUrgedTitles.map(function (t) {
                return renderTitleRow(t, NOT_URGED_BADGE_STYLE, formatMessage({
                    id: "ui-br-legaldeposits.preLitigation.badge.notUrged",
                }));
            })))),
        React.createElement("div", { className: styles.summary },
            React.createElement(MessageBanner, { type: hasSelection ? "success" : "warning" }, formatMessage({
                id: "ui-br-legaldeposits.preLitigation.selectionSummary",
            }, { count: selectedTitleIds.size }))))) : (React.createElement(MessageBanner, { type: "warning" }, formatMessage({
        id: "ui-br-legaldeposits.preLitigation.noTitles",
    })))) : (React.createElement(React.Fragment, null,
        React.createElement(RadioButtonGroup, { label: formatMessage({
                id: "ui-br-legaldeposits.enforcement.field.channel",
            }), value: channel, onChange: function (e) {
                return setChannel(e.target.value);
            } },
            React.createElement(RadioButton, { label: formatMessage({ id: CHANNEL_LABEL_IDS.POSTAL }), value: "POSTAL" }),
            React.createElement(RadioButton, { label: formatMessage({ id: CHANNEL_LABEL_IDS.DATA_BOX }), value: "DATA_BOX", disabled: !dataBoxContact })),
        !dataBoxContact && (React.createElement("div", { className: styles.hint }, formatMessage({
            id: "ui-br-legaldeposits.preLitigation.dataBoxMissing",
        }))),
        channel === "POSTAL" && (React.createElement(TextField, { label: formatMessage({
                id: "ui-br-legaldeposits.preLitigation.field.recipientAddress",
            }), value: recipientAddress, onChange: function (e) {
                return setRecipientAddress(e.target.value);
            }, required: true })),
        channel === "DATA_BOX" && dataBoxContact && (React.createElement(TextField, { label: formatMessage({
                id: "ui-br-legaldeposits.enforcement.field.dataBoxAddress",
            }), value: dataBoxContact.value, onChange: function () { return undefined; }, readOnly: true })),
        React.createElement(TextArea, { label: formatMessage({
                id: "ui-br-legaldeposits.enforcement.field.notes",
            }), value: notes, onChange: function (e) {
                return setNotes(e.target.value);
            } }),
        React.createElement(MessageBanner, { type: "success" }, formatMessage({ id: "ui-br-legaldeposits.preLitigation.recordSummary" }, { count: selectedTitleIds.size })),
        React.createElement("div", { className: styles.downloadSection },
            React.createElement(Button, { onClick: handleDownload, disabled: !hasSelection || downloadMutation.isLoading }, formatMessage({
                id: "ui-br-legaldeposits.preLitigation.button.downloadPdf",
            })))))));
};
